/**
 * Compact bulk editor for KPI definitions: add/remove tags, status, frequency.
 */

import React, { useState } from 'react';
import { AE_INPUT_COMPACT, AE_LABEL_COMPACT, AE_SELECT_COMPACT } from '@/components/assessment-engine/layout/aeTheme';
import { useLanguage } from '@/state/language/LanguageProvider';
import { bulkEditKpis } from '@/services/kpiBulkService';
import type { KpiCatalogFilterValues } from './KpiCatalogFilters';
import toast from 'react-hot-toast';

type Props = {
  selectedIds: string[];
  filters: KpiCatalogFilterValues;
  search?: string;
  onApplied: () => void;
};

const FREQUENCIES = [
  { value: '', labelKey: 'kpi.bulkNoChange' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' }
];

export const KpiBulkEditBar: React.FC<Props> = ({
  selectedIds,
  filters,
  search,
  onApplied
}) => {
  const { t } = useLanguage();
  const [addTags, setAddTags] = useState('');
  const [removeTags, setRemoveTags] = useState('');
  const [status, setStatus] = useState('');
  const [frequency, setFrequency] = useState('');
  const [applyAllMatching, setApplyAllMatching] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!applyAllMatching && selectedIds.length === 0) {
      toast.error(t('kpi.bulkNeedSelection') || 'Select KPIs or apply to all matching filters.');
      return;
    }
    if (!addTags.trim() && !removeTags.trim() && !status && !frequency) {
      toast.error(t('kpi.bulkNeedChange') || 'Choose tags, status, or frequency to change.');
      return;
    }

    setSaving(true);
    try {
      const result = await bulkEditKpis({
        ids: applyAllMatching ? undefined : selectedIds,
        applyToFiltered: applyAllMatching,
        filters: applyAllMatching
          ? {
              origin: filters.origin || undefined,
              status: filters.status || undefined,
              tag: filters.tag.trim() || undefined,
              search: search?.trim() || undefined
            }
          : undefined,
        addTags: addTags.trim() ? addTags.split(/[,;]/) : undefined,
        removeTags: removeTags.trim() ? removeTags.split(/[,;]/) : undefined,
        status: status === 'ACTIVE' || status === 'INACTIVE' ? status : undefined,
        frequency: frequency || undefined
      });
      const message = (t('kpi.bulkSuccess') || 'Updated {updated} KPIs.').replace(
        '{updated}',
        String(result.updated)
      );
      toast.success(
        result.skipped > 0
          ? `${message} Skipped ${result.skipped}.`
          : message
      );
      setAddTags('');
      setRemoveTags('');
      setStatus('');
      setFrequency('');
      onApplied();
    } catch (error) {
      console.error('KPI bulk edit failed', error);
      toast.error(t('kpi.bulkFailed') || 'Could not update KPIs.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-2">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-[var(--color-surface-text)]">
          {t('kpi.bulkEdit') || 'Bulk edit'}
          <span className="ml-2 text-xs font-normal text-[var(--color-secondary-text)]">
            {(t('kpi.bulkSelected') || '{count} selected').replace('{count}', String(selectedIds.length))}
          </span>
        </p>
        <label className="inline-flex items-center gap-1.5 text-xs text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            checked={applyAllMatching}
            onChange={(event) => setApplyAllMatching(event.target.checked)}
          />
          {t('kpi.bulkApplyAllMatching') || 'Apply to all KPIs matching current filters'}
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        <div>
          <label className={AE_LABEL_COMPACT}>{t('kpi.bulkAddTags') || 'Add tags'}</label>
          <input
            className={AE_INPUT_COMPACT}
            value={addTags}
            onChange={(event) => setAddTags(event.target.value)}
            placeholder={t('kpi.tagsHint') || 'Comma-separated labels. Matching ignores case.'}
          />
        </div>
        <div>
          <label className={AE_LABEL_COMPACT}>{t('kpi.bulkRemoveTags') || 'Remove tags'}</label>
          <input
            className={AE_INPUT_COMPACT}
            value={removeTags}
            onChange={(event) => setRemoveTags(event.target.value)}
            placeholder={t('kpi.tagsHint') || 'Comma-separated labels. Matching ignores case.'}
          />
        </div>
        <div>
          <label className={AE_LABEL_COMPACT}>{t('kpi.bulkStatus') || 'Status'}</label>
          <select
            className={AE_SELECT_COMPACT}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{t('kpi.bulkNoChange') || 'No change'}</option>
            <option value="ACTIVE">{t('kpi.filterStatusActive') || 'Active'}</option>
            <option value="INACTIVE">{t('kpi.filterStatusInactive') || 'Inactive'}</option>
          </select>
        </div>
        <div>
          <label className={AE_LABEL_COMPACT}>{t('kpi.bulkFrequency') || 'Frequency'}</label>
          <select
            className={AE_SELECT_COMPACT}
            value={frequency}
            onChange={(event) => setFrequency(event.target.value)}
          >
            {FREQUENCIES.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.labelKey ? t(option.labelKey) || 'No change' : option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            disabled={saving}
            onClick={apply}
            className="w-full rounded-md bg-[var(--color-accent-bg)] px-3 py-1.5 text-sm font-medium text-[var(--color-accent-text)] hover:opacity-90 disabled:opacity-60"
          >
            {saving ? t('common.saving') || 'Saving…' : t('kpi.bulkApply') || 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
};
